import React from 'react'
import PropTypes from 'prop-types'
import { Form, FormSpy } from '@entrata/portals-common/lib/components/forms'
import {
  EmailField,
  PasswordField,
} from '@entrata/portals-common/lib/boxed-fields'
import { StaticSection } from 'portal-modules/common/components/static-shell'
import LoginFormLinks from './LoginFormLinks'
import parseQueryString from 'portal-modules/common/utils/parse-query-string'
import emailValidator from 'portal-modules/common/utils/email-validator'
import getBrandingSettings from 'portal-modules/auth/utils/get-branding-settings'
import { StyledLoginCustomText } from 'portal-modules/common/components/atoms'
import DangerousInnerHTML from 'portal-modules/common/components/DangerousInnerHTML'
import { ModalControl } from '@entrata/portals-common/lib/components'
import { ReskinModal } from 'portal-modules/common/components/modals'
import Button from 'portal-modules/common/components/Button'
import { EmailOtpForm } from 'portal-modules/auth/components'
const { loginPageCustomText } = getBrandingSettings(window.rpSettings)
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
const { isRP, isTP } = getProjectConditions()
import styled from '@emotion/styled'

const params = parseQueryString(window.location.href)

// Styled label for form fields
const StyledFieldLabel = styled('div')`
  color: ${({ theme }) => theme.text.muted_foreground || '#9CA3AF'};
  font-size: 0.875rem;
  font-weight: 400;
  display: block;
  margin-bottom: 8px;
  line-height: 1.4;
`

// Custom field wrapper to show label above input
const StyledFieldWrapper = styled('div')`
  margin-bottom: 0;
  position: relative;
  min-height: 80px;
  
  /* Add bottom margin to the form-field wrapper */
  .boxed-field,
  .form-field {
    margin-bottom: 0;
  }
`
const initialFieldState = {
  username: {
    required: true,
    value: params.email_adress || '',
    readOnly: params.email_adress ? true : false,
  },
  password: {
    required: true,
    onCopy: e => e.preventDefault(),
    onCut: e => e.preventDefault(),
    onDragStart: e => e.preventDefault(),
  },
}

// Wrapper component that applies styled input to boxed fields for TP
const StyledBoxedFieldWrapper = styled('div')`
  /* Hide FloatingLabel inside form-field completely */
  .boxed-field > div:first-child,
  .form-field > div:first-child {
    display: none;
    height: 0;
    overflow: hidden;
  }
  
  /* Apply styled input styling to nested input elements */
  input {
    background: ${({ theme }) => theme.backgroundColor?.tp_card || '#252e42'};
    color: ${({ theme }) => theme.text.white};
    border: 1px solid ${({ theme }) => theme.fields?.border_color || theme.gray?.gray3 || '#2a3349'};
    border-radius: 6px;
    transition: all 0.2s ease;
    padding: 12px 16px;
    font-size: 0.875rem;
    line-height: 1.5;
    min-height: 48px;
    box-sizing: border-box;
    width: 100%;
    outline: none;

    &:focus {
      border-color: ${({ theme }) => theme.fields?.focus || theme.primary?.base || '#6366F1'};
      box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
    }

    &::placeholder {
      opacity: 1;
      color: ${({ theme }) => theme.text?.muted_foreground || '#6B7280'};
      font-size: 0.875rem;
    }
    
    &::-webkit-input-placeholder {
      color: ${({ theme }) => theme.text?.muted_foreground || '#6B7280'};
      opacity: 1;
    }
    
    &::-moz-placeholder {
      color: ${({ theme }) => theme.text?.muted_foreground || '#6B7280'};
      opacity: 1;
    }
    
    &:-ms-input-placeholder {
      color: ${({ theme }) => theme.text?.muted_foreground || '#6B7280'};
      opacity: 1;
    }
  }
`

// General wrapper component for non-TP boxed fields
const StyledGeneralFieldWrapper = styled('div')`
  .boxed-field > div:first-child,
  .form-field > div:first-child {
    /* Keep default behavior for non-TP */
  }
`

const StyledTPLoginFormContainer = styled('div')`
  background-color: ${({ theme }) => theme.mode.card_background_color};
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  border: 1px solid #2a334933;
  border-radius: 0.375rem;
  box-shadow: 0 4px 16px ${({ theme }) => theme.reskinMenu.shadow_color};
  padding: 24px;
  width: 419px;
  margin-left: -41px;
  
  /* Fix validation error message alignment and spacing below input */
  .error,
  .error-message,
  .validation-error,
  [class*="error"],
  .field-error {
    margin-top: 4px;
    margin-bottom: 0;
    padding: 0;
    font-size: 12px;
    color: ${({ theme }) => theme.error?.base || '#EF4444'};
    font-weight: 500;
    display: block;
    text-align: left;
    white-space: normal;
    word-wrap: break-word;
    position: relative;
    width: 100%;
  }
  
  /* Add spacing between form field wrappers for validation messages */
  ${StyledFieldWrapper} + ${StyledFieldWrapper} {
    margin-top: 40px;
  }
`
const LoginForm = props => (
  <>
    {isTP ? (
      <StyledTPLoginFormContainer>
        <Form {...props} initialFieldState={initialFieldState}>
        <StyledLoginCustomText>
          <DangerousInnerHTML>{__(loginPageCustomText)}</DangerousInnerHTML>
        </StyledLoginCustomText>
        <StyledFieldWrapper>
          <StyledFieldLabel>{__('Email')}</StyledFieldLabel>
          <StyledBoxedFieldWrapper>
            <Form.Field name='username'>
              {fieldProps => (
                <EmailField
                  {...fieldProps}
                  validate={emailValidator}
                  id='username'
                  fixedLabel
                  placeholder={__('name@company.com')}
                />
              )}
            </Form.Field>
          </StyledBoxedFieldWrapper>
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <StyledFieldLabel>{__('Password')}</StyledFieldLabel>
          <StyledBoxedFieldWrapper>
            <Form.Field name='password'>
              {fieldProps => (
                <PasswordField
                  {...fieldProps}
                  fixedLabel
                  placeholder={__('Enter your password')}
                  autoComplete='Off'
                  defaultHide
                />
              )}
            </Form.Field>
          </StyledBoxedFieldWrapper>
        </StyledFieldWrapper>
        <StaticSection.ButtonBar buttonText={__('Log In')} spacing={'40px'} />
        {isRP ? (
          <ModalControl>
            {({ onClick, ref, ...modalProps }) => (
              <>
                <Button
                  size='large'
                  appearance='secondary'
                  ref={ref}
                  onClick={onClick}
                >
                  {__('Sign In Without Password')}
                </Button>
                {modalProps.show && (
                  <ReskinModal
                    {...modalProps}
                    onClick={modalProps.onClose}
                    isFormActionRequired
                    modalId='otp-login-modal'
                  >
                    <FormSpy fields={['username']}>
                      {({ values: { username } }) => (
                        <EmailOtpForm
                          {...modalProps}
                          {...props}
                          userEmail={username}
                        />
                      )}
                    </FormSpy>
                  </ReskinModal>
                )}
              </>
            )}
          </ModalControl>
        ) : (
          <></>
        )}
      </Form>
      </StyledTPLoginFormContainer>   
    ) : (
      <Form {...props} initialFieldState={initialFieldState}>
        <StyledLoginCustomText>
          <DangerousInnerHTML>{__(loginPageCustomText)}</DangerousInnerHTML>
        </StyledLoginCustomText>
        <StyledFieldWrapper>
          <StyledFieldLabel>{__('Email')}</StyledFieldLabel>
          <StyledGeneralFieldWrapper>
            <Form.Field name='username'>
              {fieldProps => (
                <EmailField
                  {...fieldProps}
                  validate={emailValidator}
                  id='username'
                  fixedLabel
                  placeholder={__('name@company.com')}
                />
              )}
            </Form.Field>
          </StyledGeneralFieldWrapper>
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <StyledFieldLabel>{__('Password')}</StyledFieldLabel>
          <StyledGeneralFieldWrapper>
            <Form.Field name='password'>
              {fieldProps => (
                <PasswordField
                  {...fieldProps}
                  fixedLabel
                  placeholder={__('Enter your password')}
                  autoComplete='Off'
                  defaultHide
                />
              )}
            </Form.Field>
          </StyledGeneralFieldWrapper>
        </StyledFieldWrapper>
        <StaticSection.ButtonBar buttonText={__('Log In')} spacing={'40px'} />
        {isRP ? (
          <ModalControl>
            {({ onClick, ref, ...modalProps }) => (
              <>
                <Button
                  size='large'
                  appearance='secondary'
                  ref={ref}
                  onClick={onClick}
                >
                  {__('Sign In Without Password')}
                </Button>
                {modalProps.show && (
                  <ReskinModal
                    {...modalProps}
                    onClick={modalProps.onClose}
                    isFormActionRequired
                    modalId='otp-login-modal'
                  >
                    <FormSpy fields={['username']}>
                      {({ values: { username } }) => (
                        <EmailOtpForm
                          {...modalProps}
                          {...props}
                          userEmail={username}
                        />
                      )}
                    </FormSpy>
                  </ReskinModal>
                )}
              </>
            )}
          </ModalControl>
        ) : (
          <></>
        )}
      </Form>
    )}

    <LoginFormLinks />
  </>
)

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func,
}

export default LoginForm